import { CheckCircle2, AlertCircle, Activity } from 'lucide-react';

type Status = 'nominal' | 'warning' | 'compute';

interface DiagramBannerProps {
  /** Classification ID, e.g. "FLT-104 · LIVE PREVIEW". */
  id: string;
  /** Optional doc number (e.g. "SE-FLT"). */
  doc?: string;
  /** Revision letter or number (e.g. "A"). Defaults to "A". */
  rev?: string;
  /** Sheet position, e.g. "1/1". Defaults to "1/1". */
  sheet?: string;
  /** Status pill on the right side. */
  status?: Status;
  /** Optional override label for the status pill. */
  statusLabel?: string;
}

/**
 * Engineering-document classification banner that sits above every Forge
 * diagram. Standardized after the v110 diagram-polish audit so all visuals
 * read as "NASA dossier," not "archery industry."
 *
 * Visual contract (do not break):
 *   - 11 px monospace
 *   - 0.25em (≈3 px) letter-spacing
 *   - Cyan #22d3ee left-aligned ID + status pill
 *   - Zinc-500 right-aligned DOC / REV / SHEET
 *   - Thin 1px zinc-800 divider under the row
 *
 * Status colors follow the global semantic convention:
 *   nominal  → emerald  (all checks pass)
 *   warning  → amber    (something to watch)
 *   compute  → cyan     (live computation — most diagrams default here)
 */
export function DiagramBanner({
  id,
  doc,
  rev = 'A',
  sheet = '1/1',
  status = 'compute',
  statusLabel,
}: DiagramBannerProps) {
  const tone =
    status === 'nominal'
      ? 'text-emerald-400'
      : status === 'warning'
        ? 'text-amber-400'
        : 'text-cyan-400';
  const Icon =
    status === 'nominal' ? CheckCircle2 : status === 'warning' ? AlertCircle : Activity;
  const label =
    statusLabel ??
    (status === 'nominal' ? 'CONVERGED ✓' : status === 'warning' ? 'CAUTION' : 'LIVE SOLVE');

  return (
    <div
      className="flex items-center justify-between gap-3 px-3 py-1.5 border-b border-zinc-800/80 bg-zinc-950/40 rounded-t-md"
      style={{
        fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
        fontSize: '11px',
        letterSpacing: '0.18em',
      }}
      aria-hidden
    >
      <div className="flex items-center gap-2 min-w-0">
        <Icon size={11} className={`${tone} shrink-0`} />
        <span className={`${tone} truncate uppercase`}>{id}</span>
        <span className={`${tone} opacity-70 hidden sm:inline whitespace-nowrap`}>· {label}</span>
      </div>
      <div className="text-zinc-500 whitespace-nowrap shrink-0 uppercase">
        {doc ? `${doc} · ` : ''}REV {rev} · SHEET {sheet}
      </div>
    </div>
  );
}
